// src/utils/services/medicationAiService.js
import { callOpenAIChat } from './openaiClient';

// Copiamos el helper del otro servicio (o lo importas si lo exportas allí)
const safeParseJsonFromModel = (rawContent) => {
  if (!rawContent) {
    throw new Error('No content to parse');
  }

  let text = Array.isArray(rawContent)
    ? rawContent
        .map((p) =>
          typeof p === 'string'
            ? p
            : (p && (p.text || p.content || '')) || ''
        )
        .join('')
    : String(rawContent);

  text = text.trim();

  if (text.startsWith('```')) {
    text = text.replace(/^```[a-zA-Z]*\s*/m, '');
    text = text.replace(/```$/m, '');
    text = text.trim();
  }

  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');

  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    console.error('[safeParseJsonFromModel] No JSON object found in text:', text);
    throw new Error('No JSON object found in model response');
  }

  const jsonSlice = text.slice(firstBrace, lastBrace + 1);
  return JSON.parse(jsonSlice);
};

/**
 * Genera la estructura para la "Detailed Medication Table" usando IA.
 *
 * Espera un "contextText" ya construido por buildMedicationDetailContextText().
 */
export const generateMedicationDetailFromAI = async ({ contextText }) => {
  if (!contextText || !contextText.trim()) {
    console.warn('[generateMedicationDetailFromAI] Empty contextText');
    return [];
  }

  const userPrompt = `
You are a clinical pharmacist and physician assistant specialized in medication management, drug safety, and adherence assessment.

I will provide you with:
- A structured medication list from the EHR.
- Clinical notes where medications may be mentioned.
- Allergy information.

Your task is to build a concise medication summary table for ONE patient.

Return ONLY a JSON object with the following structure (no extra text):

{
  "medications": [
    {
      "name": "Medication name exactly as used clinically",
      "therapeuticClass": "Pharmacologic or therapeutic class (e.g. ACE inhibitor, Beta-blocker)",
      "prescriber": "Main prescriber name or 'N/A' if unknown",
      "lastFill": "YYYY-MM-DD or 'N/A'",
      "nextRefill": "YYYY-MM-DD or 'N/A'",
      "adherence": true,
      "adherenceReason": "Short explanation based on notes or refill dates",
      "interactions": [
        "Key interaction or safety concern 1",
        "Key interaction or safety concern 2"
      ],
      "bbw": [
        "Relevant Black Box Warning (BBW) if applicable, or leave array empty"
      ],
      "priority": "high"  // use 'high' for dangerous or critical meds, 'medium' for relevant chronic meds, 'low' for others
    }
  ]
}

Important rules:
- DO NOT write anything outside of the JSON.
- Focus on CURRENT or clinically relevant chronic medications.
- Use clinical notes AND the structured medication list to infer adherence (missed doses, gaps in refills, patient reports).
- If you do not find clear refill dates, set lastFill and nextRefill to "N/A" but still try to comment adherence using narrative notes.
- Interactions:
  - Highlight only the most important potential drug-drug or drug-allergy interactions.
  - Mention if the interaction is "monitor only" or "avoid combination if possible".
- BBW:
  - Only include true FDA Black Box Warnings.
  - If none are clearly applicable, use an empty array [].
- If information is missing, use "N/A" instead of guessing.

===== PATIENT MEDICATION CONTEXT =====

${contextText}
`;

  const data = await callOpenAIChat({
    model: 'gpt-4o-mini',
    messages: [{ role: 'user', content: userPrompt }],
    temperature: 0.2,
  });

  const content = data?.choices?.[0]?.message?.content;
  console.log('[generateMedicationDetailFromAI] RAW content from OpenAI:', content);

  if (!content) {
    console.error('[generateMedicationDetailFromAI] Empty response from OpenAI');
    throw new Error('Empty response from OpenAI');
  }

  try {
    const json = safeParseJsonFromModel(content);

    if (!json.medications || !Array.isArray(json.medications)) {
      console.error(
        '[generateMedicationDetailFromAI] Unexpected JSON format:',
        json
      );
      throw new Error('Unexpected JSON format (missing "medications")');
    }

    // 🔧 Sanity check / normalización mínima para el UI
    return json.medications.map((m) => ({
      name: m.name || 'N/A',
      therapeuticClass: m.therapeuticClass || 'N/A',
      prescriber: m.prescriber || 'N/A',
      lastFill: m.lastFill || 'N/A',
      nextRefill: m.nextRefill || 'N/A',
      adherence: Boolean(m.adherence),
      adherenceReason: m.adherenceReason || '',
      interactions: Array.isArray(m.interactions) ? m.interactions : [],
      bbw: Array.isArray(m.bbw) ? m.bbw : [],
      priority: m.priority || 'medium',
    }));
  } catch (err) {
    console.error('[generateMedicationDetailFromAI] Error parsing JSON:', err);
    return [];
  }
};
